import SPS, {
  SpsPublication,
  type Poster,
  type SpsRequestOptions,
  type DefaultSalesposterParams,
} from '@schibsted/sps-sdk';

import { ensureClientBucketCookie } from './utils';
import { Identity } from '@schibsted/account-sdk-browser/src/identity';

export type Section = 'default' | 'sport' | 'total';

// see https://sp-manager.ma.schibsted.digital/publication/3eea0187-f0e4-4b04-b79d-0f988f43f587/placements
export const accessLevelToSpsSection = (
  accessLevel: 'plus' | 'sport' | 'total'
): Section => {
  switch (accessLevel) {
    case 'sport':
      return 'sport';
    case 'total':
      return 'total';
    default:
      return 'default';
  }
};

export interface Salesposter {
  html: string;
  campaign: string;
  iframeId?: string;
}

export type SpsApiClient = ReturnType<typeof createSpsApi>;

// SPS SDK typings require `salesposter` requests to provide a `fallbackPoster` +
// `showFallbackPosterOnNoContent: true`, guaranteeing a non-null `Poster`. We
// deliberately omit those (no fallback content) and handle the `null` case
// ourselves, so we widen the call signature to the shape that matches at runtime.
type GetSalesposterPoster = (
  placementType: 'salesposter',
  props: { params: DefaultSalesposterParams } & SpsRequestOptions<'salesposter'>
) => Promise<Poster<'salesposter'> | null>;

export const createSpsApi = (
  publication: SpsPublication,
  domain: string,
  isTpaasEnabled = false
) => {
  const sps = new SPS({
    publication,
    domain,
    environment: 'production',
  });

  const getPoster = sps.getPoster as unknown as GetSalesposterPoster;

  const getSalesposter = async ({
    section = 'default',
    placement,
    userId,
    refId,
    entityId,
    redirectUri,
    getLoginUrl,
  }: {
    section?: string;
    placement: string;
    userId?: string | null;
    refId?: string;
    entityId: string;
    redirectUri?: string | null;
    getLoginUrl: Identity['loginUrl'];
  }): Promise<Salesposter> => {
    ensureClientBucketCookie();

    const poster = await getPoster('salesposter', {
      params: {
        section,
        articleType: placement,
        articleId: entityId,
        refId: refId ?? entityId,
        tags: [],
        ...(userId ? { spid: userId } : {}),
      },
      pulseEvents: {
        legacy: true,
        tpaas: isTpaasEnabled,
      },
      tracking: {
        contentId: entityId,
        contentType: 'video',
      },
      generateRedirectUrl: () => {
        const url = redirectUri
          ? new URL(redirectUri)
          : new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('state', window.location.pathname);
        return url.toString();
      },
      generateLoginUrl: ({ campaign }) => {
        const state = encodeURIComponent(window.location.href);
        return `${getLoginUrl({ state })}&utm_campaign=${campaign}`;
      },
      appendSearchParams: true,
    });

    if (!poster) {
      throw new Error('SPS returned no salesposter content');
    }

    return {
      html: `<base target="_top"> ${poster.content}`,
      campaign: poster.campaign,
      iframeId: poster.iframeId,
    };
  };

  return {
    getSalesposter,
  };
};
